import { Box, Button, Dialog, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack } from "@mui/material"
import { StyledDialogContent, StyledDialogFooter, StyledDialogTitle } from "../../../../components/Dialog.styled"
import { useEffect, useMemo, useState } from "react"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs, { Dayjs } from "dayjs"
import CloseModalButton from '../../../../components/CloseModalButton'

interface FormData {
  fromDate: Dayjs | null
  toDate: Dayjs | null
  downloadType: string
}

const initialFormDataState = {
  fromDate: null,
  toDate: null,
  downloadType: "All"
}

const BiologicalExportModal = ({ open, handleClose, handleSubmit, fromDate, toDate }: {
  open: boolean,
  handleClose: () => void,
  handleSubmit: (fromDate?: Dayjs, toDate?: Dayjs) => void,
  fromDate: Dayjs,
  toDate: Dayjs,
}) => {
  const [formData, setFormData] = useState<FormData>(initialFormDataState)
  const [formErrors, setFormErrors] = useState({ toDate: false, fromDate: false })

  const handleEditChange = (field: keyof FormData, value: Dayjs | string | null) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }))
    if (value && value <= dayjs()) {
      setFormErrors({ ...formErrors, [field]: false })
    } else {
      setFormErrors({ ...formErrors, [field]: true })
    }
  }


  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      fromDate,
      toDate
    }))
  }, [fromDate?.valueOf(), toDate?.valueOf()])

  const formValid = () => {
    if (formData.downloadType === 'All') return true
    return !formErrors.toDate && !formErrors.fromDate
  }

  const submitForm = () => {
    if (formData.downloadType === 'All') {
      handleSubmit()
      return
    }
    handleSubmit(formData.fromDate as Dayjs, formData.toDate as Dayjs)
    setFormData({ ...initialFormDataState, fromDate, toDate })
  }

  const onClose = () => {
    setFormData({ ...initialFormDataState, fromDate, toDate })
    handleClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <CloseModalButton handleClose={onClose} />
      <StyledDialogTitle>Download Spore Test</StyledDialogTitle>
      <StyledDialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl>
            <FormLabel id="file-type-label">File Type</FormLabel>
            <RadioGroup
              aria-labelledby="file-type-label"
              defaultValue="XLS"
              name="file-type"
            >
              <FormControlLabel value="XLS" control={<Radio checked />} label="XLS" />
            </RadioGroup>
          </FormControl>
          <FormControl>
            <FormLabel id="download-label">Download</FormLabel>
            <RadioGroup
              aria-labelledby="download-label"
              defaultValue="All"
              name="download"
              onChange={(e) => handleEditChange('downloadType', e.target.value)}
            >
              <FormControlLabel value="All" control={<Radio checked={formData.downloadType === 'All'} />} label="All" />
              <FormControlLabel value="Date Range" control={<Radio checked={formData.downloadType === 'Date Range'} />} label="Date Range" />
            </RadioGroup>
          </FormControl>
          {formData.downloadType === 'Date Range' && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <FormControl size='small'>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="en"
                >
                  <DatePicker
                    label="From"
                    value={dayjs(formData?.fromDate) ?? ''}
                    onChange={(date) => handleEditChange('fromDate', date)}
                    format="MM-DD-YYYY"
                    disableFuture
                    slotProps={{
                      textField: { size: 'small' }
                    }}
                  />
                </LocalizationProvider>
              </FormControl>
              <FormControl size='small'>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="en"
                >
                  <DatePicker
                    label="To"
                    value={dayjs(formData?.toDate) ?? ''}
                    onChange={(date) => handleEditChange('toDate', date)}
                    format="MM-DD-YYYY"
                    disableFuture
                    slotProps={{
                      textField: { size: 'small' }
                    }}
                  />
                </LocalizationProvider>
              </FormControl>
            </Box>
          )}
        </Stack>
      </StyledDialogContent>
      <StyledDialogFooter>
        <Button variant='outlined' onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submitForm} disabled={!formValid()}>Submit</Button>
      </StyledDialogFooter>
    </Dialog>
  )
}

export default BiologicalExportModal